import { PlusOutlined } from '@ant-design/icons';
import type { FormInstance } from 'antd';
import { Button, Form, Space, Tag, Typography } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BUSINESS_RELATION_TYPE_LABELS,
  BusinessRelationSelectorModal,
  type BusinessRelationValueItem,
} from '@/modules/zy-crm/components/business-relation-selector-modal';
import { resolveCrmScopedViewModeFromScope } from '@/modules/zy-crm/utils/crm-view';
import { resolveCrmPageScopeFromPathname } from '@/modules/zy-crm/utils/data-scope';
import { queryContactDetail } from '@/services/bff/contact.api';
import { queryCrmResource } from '@/services/bff/crm.api';
import { queryCustomerDetail } from '@/services/bff/customer.api';
import { queryLeadDetail } from '@/services/bff/lead.api';
import { queryOpportunityDetail } from '@/services/bff/opportunity.api';
import type {
  CalendarBizType,
  CalendarEventRelationItem,
} from '@/services/bff/types/calendar';

export type CalendarRelationFormValue = {
  bizType?: CalendarBizType;
  bizUuid?: string;
};

type CalendarRelationFieldsProps = {
  form: FormInstance<any>;
  readOnly?: boolean;
};

const normalizeBizUuid = (value: unknown) => String(value || '').trim();

const normalizeDisplayText = (value: unknown) => String(value || '').trim();

const buildRelationKey = (bizType?: CalendarBizType, bizUuid?: string) =>
  bizType && bizUuid ? `${bizType}:${bizUuid}` : '';

const resolveCurrentPathname = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.location?.pathname || '';
};

const resolveResourceLabel = async (
  bizType: CalendarBizType,
  bizUuid: string,
  pageScope: 'self' | 'all',
) => {
  if (bizType === 'lead') {
    const detail = await queryLeadDetail(bizUuid, { scope: pageScope });
    return normalizeDisplayText(detail?.name);
  }
  if (bizType === 'customer') {
    const detail = await queryCustomerDetail(bizUuid, {
      viewMode: resolveCrmScopedViewModeFromScope(pageScope),
    });
    return normalizeDisplayText(detail?.name);
  }
  if (bizType === 'contact') {
    const detail = await queryContactDetail(bizUuid, {
      viewMode: resolveCrmScopedViewModeFromScope(pageScope),
    });
    return normalizeDisplayText(detail?.name);
  }
  if (bizType === 'opportunity') {
    const detail = await queryOpportunityDetail(bizUuid, {
      viewMode: resolveCrmScopedViewModeFromScope(pageScope),
    });
    return normalizeDisplayText(detail?.name);
  }

  const resourcePathMap: Record<
    Extract<CalendarBizType, 'quote' | 'contract' | 'receipt'>,
    string
  > = {
    quote: 'quotes',
    contract: 'contracts',
    receipt: 'receipts',
  };
  const page = await queryCrmResource<Record<string, unknown>>(
    resourcePathMap[bizType],
    {
      page: 1,
      pageSize: 1,
      uuid: bizUuid,
    } as any,
  );
  const record = page?.list?.[0];
  return normalizeDisplayText(record?.name || record?.code || record?.title);
};

const toDisplayLabel = (
  item: CalendarRelationFormValue,
  labelMap: Record<string, string>,
) => {
  const bizType = item.bizType;
  const bizUuid = normalizeBizUuid(item.bizUuid);
  if (!bizType || !bizUuid) {
    return '';
  }
  return `${BUSINESS_RELATION_TYPE_LABELS[bizType]} ${
    labelMap[buildRelationKey(bizType, bizUuid)] || bizUuid
  }`;
};

export const createCalendarRelationFormValue = (
  bizType?: CalendarBizType,
  bizUuid?: string,
): CalendarRelationFormValue => ({
  bizType,
  bizUuid,
});

export const toCalendarRelationFormValues = (
  relations?: CalendarEventRelationItem[],
): CalendarRelationFormValue[] =>
  (relations || []).map((item) => ({
    bizType: item.bizType,
    bizUuid: item.bizUuid,
  }));

export const normalizeCalendarRelationPayload = (
  relations?: CalendarRelationFormValue[],
): CalendarEventRelationItem[] | undefined => {
  const output: CalendarEventRelationItem[] = [];
  const seen = new Set<string>();

  for (const item of relations || []) {
    const bizType = item?.bizType;
    const bizUuid = normalizeBizUuid(item?.bizUuid);
    if (!bizType || !bizUuid) {
      continue;
    }
    const key = `${bizType}:${bizUuid}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    output.push({
      bizType,
      bizUuid,
      relationRole: output.length === 0 ? 'primary' : 'collaborative',
    });
  }

  return output.length ? output : undefined;
};

export function CalendarRelationFields({
  form,
  readOnly = false,
}: CalendarRelationFieldsProps) {
  const pageScope = resolveCrmPageScopeFromPathname(resolveCurrentPathname());
  const [modalOpen, setModalOpen] = useState(false);
  const [labelMap, setLabelMap] = useState<Record<string, string>>({});
  const requestIdRef = useRef(0);
  const labelMapRef = useRef<Record<string, string>>({});
  const relations =
    (Form.useWatch('relations', form) as
      | CalendarRelationFormValue[]
      | undefined) || [];

  const normalizedRelations = useMemo(
    () =>
      (relations || []).filter(
        (item) => item?.bizType && normalizeBizUuid(item?.bizUuid),
      ),
    [relations],
  );

  useEffect(() => {
    labelMapRef.current = labelMap;
  }, [labelMap]);

  useEffect(() => {
    if (!normalizedRelations.length) {
      if (Object.keys(labelMapRef.current).length > 0) {
        labelMapRef.current = {};
        setLabelMap({});
      }
      return;
    }

    const pendingItems = normalizedRelations.filter((item) => {
      const bizUuid = normalizeBizUuid(item.bizUuid);
      return !labelMapRef.current[buildRelationKey(item.bizType, bizUuid)];
    });

    if (!pendingItems.length) {
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    void Promise.all(
      pendingItems.map(async (item) => {
        const bizType = item.bizType as CalendarBizType;
        const bizUuid = normalizeBizUuid(item.bizUuid);
        const key = buildRelationKey(bizType, bizUuid);
        try {
          const label = await resolveResourceLabel(bizType, bizUuid, pageScope);
          return [key, label || bizUuid] as const;
        } catch (error) {
          console.error(error);
          return [key, bizUuid] as const;
        }
      }),
    ).then((entries) => {
      if (requestId !== requestIdRef.current) {
        return;
      }
      const nextEntries = Object.fromEntries(entries);
      const current = labelMapRef.current;
      const hasChanged = Object.entries(nextEntries).some(
        ([key, value]) => current[key] !== value,
      );
      if (!hasChanged) {
        return;
      }
      const nextLabelMap = {
        ...current,
        ...nextEntries,
      };
      labelMapRef.current = nextLabelMap;
      setLabelMap(nextLabelMap);
    });
  }, [normalizedRelations, pageScope]);

  return (
    <>
      <Form.Item name="relations" hidden>
        <input type="hidden" />
      </Form.Item>
      <Form.Item
        label="关联业务"
        extra={
          <Typography.Text type="secondary">可添加多个业务对象</Typography.Text>
        }
      >
        <Space orientation="vertical" size={12} style={{ width: '100%' }}>
          {readOnly ? null : (
            <Button icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
              选择关联数据
            </Button>
          )}
          {normalizedRelations.length ? (
            <Space wrap size={[8, 8]}>
              {normalizedRelations.map((item) => {
                const key = `${item.bizType}:${normalizeBizUuid(item.bizUuid)}`;
                return (
                  <Tag
                    key={key}
                    closable={!readOnly}
                    onClose={(event) => {
                      event.preventDefault();
                      form.setFieldValue(
                        'relations',
                        normalizedRelations.filter(
                          (current) =>
                            !(
                              current.bizType === item.bizType &&
                              normalizeBizUuid(current.bizUuid) ===
                                normalizeBizUuid(item.bizUuid)
                            ),
                        ),
                      );
                    }}
                  >
                    {toDisplayLabel(item, labelMap)}
                  </Tag>
                );
              })}
            </Space>
          ) : (
            <Typography.Text type="secondary">
              {readOnly ? '—' : '请选择关联业务数据'}
            </Typography.Text>
          )}
        </Space>
      </Form.Item>
      {readOnly ? null : (
        <BusinessRelationSelectorModal
          open={modalOpen}
          value={normalizedRelations as BusinessRelationValueItem[]}
          defaultBizType={normalizedRelations[0]?.bizType || 'lead'}
          onCancel={() => setModalOpen(false)}
          onOk={(items) => {
            form.setFieldValue(
              'relations',
              items.map((item) => ({
                bizType: item.bizType,
                bizUuid: item.bizUuid,
              })),
            );
            setModalOpen(false);
          }}
        />
      )}
    </>
  );
}
